/**
 * Copy + arithmetic for the DJ sidebars' library meter (DjLibraryMeter.jsx):
 * "X of Y in MixGoat", the three bar segments, and the one line that says why
 * the rest is missing. Pure so the wording is unit-testable — this line is
 * the fix for a real misread (a half-filled coverage bar under "Last synced"
 * read as a stalled import for ten minutes, 2026-09-03), so its states are
 * pinned by tests rather than eyeballed.
 *
 * Coverage shape (djapp:importCoverage): { total, imported, excluded,
 * excludedPlaylists, excludedLoose } — see importCoverage() in
 * src/backend/importedPlaylistReconciler.js for the attribution rules.
 */

export const fmt = (x) => Number(x || 0).toLocaleString();
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

/**
 * Bar segments. `liveDone` = tracks a running import created so far, added
 * on top of the last recount so the fill grows during the run instead of
 * sitting stale until the recount lands.
 *   imported  cyan fill (left)
 *   excluded  hatched (right) — left out of sync on purpose
 *   pending   the dark gap between — selected, still to import
 * At rest with everything selected in, there is no gap: that is the whole
 * point (an empty stretch of track reads as "still loading").
 */
export function meterState(coverage, { liveDone = 0 } = {}) {
    if (!coverage || !coverage.total) return null;
    const total = coverage.total;
    const imported = Math.min(total, (coverage.imported || 0) + Math.max(0, liveDone || 0));
    const excluded = Math.max(0, Math.min(total - imported, coverage.excluded || 0));
    const pending = total - imported - excluded;
    return {
        total, imported, excluded, pending,
        fillPct: (imported / total) * 100,
        excludedPct: (excluded / total) * 100,
    };
}

/** "137 loose tracks not synced" / "42 tracks in 2 crates + loose tracks not synced" — null when nothing is excluded. */
export function notSyncedText(coverage, noun = 'playlist') {
    const excluded = coverage?.excluded || 0;
    if (excluded <= 0) return null;
    const pl = coverage.excludedPlaylists || 0;
    const loose = !!coverage.excludedLoose;
    if (loose && !pl) return `${fmt(excluded)} loose ${plural(excluded, 'track')} not synced`;
    const where = pl ? `${pl} ${plural(pl, noun)}${loose ? ' + loose tracks' : ''}` : 'loose tracks';
    return `${fmt(excluded)} ${plural(excluded, 'track')} in ${where} not synced`;
}

/**
 * The line under the bar: { text, action: 'manage' | null } or null for
 * "nothing to say" (everything selected is in, nothing excluded).
 *
 * During a run it is live progress and never offers the link: new tracks
 * landing ("Importing 87 of 164 new tracks…"), a re-sync that is only
 * matching what is already there ("Checking 120 of 301 tracks…"), or the
 * moment before the first tick ("Re-syncing…").
 * At rest: what is selected but not in yet, then what was left out by
 * choice — the latter with the exit ("Sync them" → Manage imports).
 */
export function meterLine(coverage, { importing = false, progress = null, noun = 'playlist' } = {}) {
    if (importing) {
        if (progress && progress.newTotal > 0) {
            return { text: `Importing ${fmt(progress.newDone || 0)} of ${fmt(progress.newTotal)} new tracks…`, action: null };
        }
        if (progress && progress.total > 0) {
            return { text: `Checking ${fmt(progress.current || 0)} of ${fmt(progress.total)} tracks…`, action: null };
        }
        return { text: 'Re-syncing…', action: null };
    }
    const st = meterState(coverage);
    if (!st) return null;
    const parts = [];
    if (st.pending > 0) parts.push(`${fmt(st.pending)} ${plural(st.pending, 'track')} not imported yet`);
    const ns = notSyncedText(coverage, noun);
    if (ns) parts.push(ns);
    if (!parts.length) return null;
    return { text: parts.join(' · '), action: ns ? 'manage' : null };
}
